import {
	Box,
	Button,
	Checkbox,
	Flex,
	FormControl,
	FormErrorMessage,
	Heading,
	Image,
	Input,
	Link,
	Text,
	VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FormEvent, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	getTextUpdatesLocation,
	getTextUpdatesShop,
} from '../../data/textUpdatesShops';

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(12px); }
	to { opacity: 1; transform: translateY(0); }
`;

const checkPop = keyframes`
	0% { transform: scale(0.6); opacity: 0; }
	60% { transform: scale(1.08); opacity: 1; }
	100% { transform: scale(1); opacity: 1; }
`;

const buttonReady = keyframes`
	0% { box-shadow: 0 0 0 0 rgba(31, 111, 178, 0.45); }
	70% { box-shadow: 0 0 0 10px rgba(31, 111, 178, 0); }
	100% { box-shadow: 0 0 0 0 rgba(31, 111, 178, 0); }
`;

function digitsOnly(value: string): string {
	return value.replace(/\D/g, '').slice(0, 10);
}

/** Format as (XXX) XXX-XXXX while typing. */
function formatPhoneDisplay(digits: string): string {
	if (digits.length === 0) return '';
	if (digits.length < 4) return `(${digits}`;
	if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidUsPhone(digits: string): boolean {
	return digits.length === 10;
}

const TextUpdatesOptIn = () => {
	const { shopSlug, accessToken } = useParams<{ shopSlug: string; accessToken: string }>();
	const [searchParams] = useSearchParams();
	const locSlug = searchParams.get('loc');

	const shop = useMemo(
		() => getTextUpdatesShop(shopSlug, accessToken),
		[shopSlug, accessToken]
	);
	const location = useMemo(
		() => (shop ? getTextUpdatesLocation(shop, locSlug) : null),
		[shop, locSlug]
	);

	const [phoneDigits, setPhoneDigits] = useState('');
	const [consent, setConsent] = useState(false);
	const [touched, setTouched] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const phoneError = touched && !isValidUsPhone(phoneDigits);
	const consentError = touched && !consent;
	const canSubmit = isValidUsPhone(phoneDigits) && consent;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setTouched(true);
		if (!canSubmit || !shop) return;

		// Frontend-only for now — log the payload shape the backend will accept later.
		console.log('[text-updates opt-in]', {
			shopSlug,
			accessToken,
			location: locSlug,
			phone: `+1${phoneDigits}`,
			consent: true,
			sourceUrl: window.location.href,
			submittedAt: new Date().toISOString(),
		});

		setSubmitted(true);
	};

	if (!shop) {
		return (
			<Flex minH="100vh" align="center" justify="center" bg="#F4F6F8" px={4}>
				<VStack spacing={3} textAlign="center" maxW="md">
					<Heading size="md" color="gray.800">
						Page not found
					</Heading>
					<Text color="gray.600" fontSize="sm">
						This text updates link is invalid or no longer available.
					</Text>
				</VStack>
			</Flex>
		);
	}

	const accent = shop.accent;
	const accentSoft = shop.accentSoft;
	const locationLabel = location?.name ?? null;

	return (
		<Flex
			minH="100vh"
			w="100%"
			position="relative"
			overflow="hidden"
			bg="#F2F5F7"
			align="center"
			justify="center"
			px={{ base: 4, md: 8 }}
			py={{ base: 10, md: 16 }}
		>
			{/* Atmospheric backdrop */}
			<Box
				position="absolute"
				inset={0}
				pointerEvents="none"
				bgImage={`
					radial-gradient(ellipse 80% 60% at 15% 10%, ${accentSoft} 0%, transparent 55%),
					radial-gradient(ellipse 70% 50% at 90% 85%, rgba(27, 77, 110, 0.08) 0%, transparent 50%),
					linear-gradient(165deg, #F2F5F7 0%, #E8EEF2 45%, #DEE6EC 100%)
				`}
			/>
			<Box
				position="absolute"
				inset={0}
				pointerEvents="none"
				opacity={0.35}
				bgImage={`url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`}
				mixBlendMode="multiply"
			/>

			<Box
				position="relative"
				w="100%"
				maxW="420px"
				animation={`${fadeUp} 0.55s ease-out both`}
			>
				{submitted ? (
					<VStack spacing={6} align="stretch" textAlign="center">
						<Flex justify="center">
							<Flex
								w="64px"
								h="64px"
								borderRadius="full"
								bg={accentSoft}
								color={accent}
								align="center"
								justify="center"
								fontSize="2xl"
								fontWeight="bold"
								animation={`${checkPop} 0.45s ease-out both`}
								aria-hidden
							>
								✓
							</Flex>
						</Flex>
						<VStack spacing={3}>
							<Heading
								as="h1"
								fontSize={{ base: '2xl', md: '3xl' }}
								fontWeight="700"
								letterSpacing="-0.02em"
								color="gray.900"
								lineHeight="1.15"
							>
								You&apos;re signed up
							</Heading>
							<Text color="gray.600" fontSize="md" lineHeight="1.55">
								We&apos;ll text{' '}
								<Text as="span" fontWeight="600" color="gray.800">
									{formatPhoneDisplay(phoneDigits)}
								</Text>{' '}
								with repair status updates and let you know when your vehicle is
								ready.
							</Text>
						</VStack>
						<Text color="gray.500" fontSize="sm" lineHeight="1.55" pt={1}>
							Reply STOP to any message to opt out.
							{shop.contactPhone ? (
								<>
									{' '}
									Questions? Call{' '}
									<Text as="span" fontWeight="600" color="gray.700">
										{formatPhoneDisplay(shop.contactPhone)}
									</Text>
									.
								</>
							) : null}
						</Text>
					</VStack>
				) : (
					<VStack
						as="form"
						onSubmit={handleSubmit}
						spacing={8}
						align="stretch"
						noValidate
					>
						<VStack spacing={5} align="stretch">
							{shop.logo ? (
								<Image
									src={shop.logo}
									alt={shop.name}
									maxH="72px"
									maxW="280px"
									w="auto"
									objectFit="contain"
									objectPosition="left center"
									animation={`${fadeUp} 0.5s ease-out both`}
								/>
							) : (
								<Flex
									w="56px"
									h="56px"
									borderRadius="14px"
									bg={accent}
									color="white"
									align="center"
									justify="center"
									fontSize="lg"
									fontWeight="700"
									letterSpacing="0.02em"
									boxShadow="none"
									animation={`${fadeUp} 0.5s ease-out both`}
								>
									{shop.initials}
								</Flex>
							)}

							<VStack spacing={2} align="stretch">
								{locationLabel && (
									<Text
										fontSize="sm"
										fontWeight="600"
										letterSpacing="0.04em"
										textTransform="uppercase"
										color={accent}
									>
										{shop.logo ? locationLabel : `${shop.name} · ${locationLabel}`}
									</Text>
								)}
								{!locationLabel && !shop.logo && (
									<Text
										fontSize="sm"
										fontWeight="600"
										letterSpacing="0.04em"
										textTransform="uppercase"
										color={accent}
									>
										{shop.name}
									</Text>
								)}
								<Heading
									as="h1"
									fontSize={{ base: '2.4rem', md: '2.75rem' }}
									fontWeight="700"
									letterSpacing="-0.03em"
									color="gray.900"
									lineHeight="1.1"
								>
									Get text updates
								</Heading>
								<Text color="gray.600" fontSize="md" lineHeight="1.55" maxW="36ch">
									Opt in to receive repair status and pickup alerts from{' '}
									{shop.name}
									{locationLabel ? ` in ${locationLabel}` : ''}.
								</Text>
							</VStack>
						</VStack>

						<VStack spacing={5} align="stretch">
							<FormControl isInvalid={phoneError}>
								<Text
									as="label"
									htmlFor="text-updates-phone"
									fontSize="sm"
									fontWeight="600"
									color="gray.700"
									mb={1.5}
									display="block"
								>
									Mobile number
								</Text>
								<Input
									id="text-updates-phone"
									type="tel"
									inputMode="numeric"
									autoComplete="tel-national"
									placeholder="(555) 123-4567"
									value={formatPhoneDisplay(phoneDigits)}
									onChange={(e) => setPhoneDigits(digitsOnly(e.target.value))}
									onBlur={() => setTouched(true)}
									size="lg"
									bg="white"
									borderColor="gray.300"
									borderRadius="10px"
									fontSize="md"
									h="52px"
									_hover={{ borderColor: 'gray.400' }}
									_focus={{
										borderColor: accent,
										boxShadow: 'none',
									}}
								/>
								{phoneError && (
									<FormErrorMessage>Enter a valid 10-digit mobile number.</FormErrorMessage>
								)}
							</FormControl>

							<FormControl isInvalid={consentError}>
								<Checkbox
									isChecked={consent}
									onChange={(e) => setConsent(e.target.checked)}
									alignItems="flex-start"
									spacing={3}
									sx={{
										'.chakra-checkbox__control': {
											mt: '3px',
											borderRadius: '4px',
											borderColor: 'gray.400',
											_checked: {
												bg: accent,
												borderColor: accent,
											},
										},
									}}
								>
									<Text fontSize="sm" color="gray.600" lineHeight="1.5">
										I agree to receive recurring automated text messages from{' '}
										{shop.name} at the number provided, about my vehicle repair
										status and pickup. Consent is not a condition of purchase.
										Message frequency varies. Msg &amp; data rates may apply.
										Reply STOP to cancel, HELP for help.
										{shop.privacyPolicyUrl && (
											<>
												{' '}
												View our terms and privacy policy{' '}
												<Link
													href={shop.privacyPolicyUrl}
													isExternal
													color={accent}
													fontWeight="600"
													textDecoration="underline"
													onClick={(e) => e.stopPropagation()}
												>
													here
												</Link>
												.
											</>
										)}
									</Text>
								</Checkbox>
								{consentError && (
									<FormErrorMessage mt={2}>
										Please confirm consent to continue.
									</FormErrorMessage>
								)}
							</FormControl>

							<Button
								key={canSubmit ? 'ready' : consent ? 'consent' : 'idle'}
								type="submit"
								size="lg"
								h="52px"
								borderRadius="10px"
								bg={consent || canSubmit ? accent : 'gray.300'}
								color="white"
								fontWeight="600"
								fontSize="md"
								opacity={canSubmit ? 1 : consent ? 0.72 : 0.45}
								boxShadow="none"
								animation={canSubmit ? `${buttonReady} 0.7s ease-out` : undefined}
								_hover={
									canSubmit
										? { bg: '#185A91', transform: 'translateY(-1px)' }
										: { bg: consent ? accent : 'gray.300' }
								}
								_active={
									canSubmit
										? { bg: '#144A78', transform: 'translateY(0)' }
										: undefined
								}
								transition="background 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease"
								isDisabled={!canSubmit}
								_disabled={{
									cursor: 'not-allowed',
									transform: 'none',
								}}
							>
								{canSubmit ? 'Opt in to texts' : consent ? 'Enter your number' : 'Opt in to texts'}
							</Button>
						</VStack>
					</VStack>
				)}
			</Box>
		</Flex>
	);
};

export default TextUpdatesOptIn;
